// The whole game rules as a pure, seeded simulation. No DOM, no clock: the
// app steps it with fixed dt and applies inputs between steps, so a seed plus
// an input log replays a run exactly (daily exams, share links, tests).

import type { Rng } from '@/core/rng';
import {
  BOSS,
  BOSSES,
  FEINT,
  GRAPPLE,
  KICK,
  KINDS,
  PATTERN,
  PATTERNS,
  TUNING as T,
  gait,
  toward,
  windupFor,
  type BossId,
  type EnemyKind,
} from './tuning';
import { eligiblePatterns, planPattern, type PlannedSpawn } from './patterns';

/**
 * Bumped whenever the same seed would bring different opponents, or a
 * different impact test (the meter, the senpai's steps), for challenge links.
 */
export const SIM_VERSION = 4;

export type Side = -1 | 1;
export type Judgement = 'perfect' | 'good' | 'counter';

export type EnemyState = 'walk' | 'feint' | 'windup' | 'launch' | 'recoil' | 'dead';

export interface Enemy {
  id: number;
  kind: EnemyKind;
  side: Side;
  /** Distance from the player, always > 0. */
  d: number;
  speed: number;
  hp: number;
  state: EnemyState;
  stateT: number;
  feinted: boolean;
  /** Distance the enemy still has to be pushed back while recoiling. */
  recoilLeft: number;
  /** Footwork phase (s): a pattern's members share one so their spacing holds. */
  phase?: number;
  /** Windup length (s): set as it enters, by that level (windupFor), and kept. */
  windup: number;
  /** A stage boss: `kind` is the act he's fighting as now (BOSSES[boss].acts[act]),
   *  `actHits` the hits that act still takes; `hp` counts all of his acts. */
  boss?: BossId;
  act?: number;
  actHits?: number;
}

/** One round of a match. Exams are three fixed rounds; Kumite is one endless ramp. */
export interface RoundSpec {
  /** Difficulty level (walk speed, arrival gap). */
  level: number;
  /** Opponents in the round, or null for endless. */
  count: number | null;
  /** Endless only: add one level every TUNING.killsPerLevel kills. */
  ramp: boolean;
  /** Spawn weights; omitted means the level-based default mix. */
  kinds?: Partial<Record<EnemyKind, number>>;
  /** Chance that an arrival is an attack pattern instead of one opponent (0–1). */
  patterns?: number;
  /** Seconds per beat of the round's music; pattern rhythms follow it. */
  beat?: number;
  /** A stage boss who enters alone once the round's opponents are down. */
  boss?: BossId;
}

export const ENDLESS: RoundSpec = { level: 0, count: null, ramp: true };

/** Match-wide rule changes (the daily exam's rule of the day). */
export interface SimOptions {
  /** Hearts at the start, and the most healing gives back. */
  hearts?: number;
  /** Multiplies how fast the Kiai meter fills. */
  kiaiRate?: number;
}

export type PlayerState = 'idle' | 'strike' | 'recover' | 'hurt';

export type SimEvent =
  | { type: 'spawn'; enemy: Enemy }
  | {
      type: 'hit';
      enemy: Enemy;
      judgement: Judgement;
      points: number;
      killed: boolean;
      /** The PERFECT chain after this hit (0 after a GOOD). */
      chain: number;
    }
  | { type: 'blocked'; enemy: Enemy }
  | { type: 'whiff'; side: Side }
  | { type: 'hurt'; enemy: Enemy; hearts: number }
  | { type: 'feint'; enemy: Enemy }
  | { type: 'windup'; enemy: Enemy }
  /** A boss took his act's last hit: knocked back, he comes in as the next. */
  | { type: 'act'; enemy: Enemy }
  | { type: 'kiai'; killed: number; points: number }
  | { type: 'roundclear' }
  | { type: 'gameover' };

export interface SimState {
  time: number;
  running: boolean;
  hearts: number;
  score: number;
  combo: number;
  bestCombo: number;
  /** Unbroken PERFECT/COUNTER hits: a GOOD, a whiff, a block or a hit taken ends it. */
  chain: number;
  bestChain: number;
  kills: number;
  /** Strikes that connected (heavies take two). */
  hits: number;
  /** PERFECT hits plus COUNTERs: the timing-quality numerator. */
  perfects: number;
  kiai: number; // 0..1
  player: { state: PlayerState; stateT: number; side: Side; strikes: number };
  invuln: number;
  enemies: Enemy[];
  level: number;
  /** Opponents still to come this round (spawned or not); null when endless. */
  remaining: number | null;
  /** True while a round is being fought; false once it's cleared. */
  inRound: boolean;
}

export function levelFor(kills: number): number {
  return Math.floor(kills / T.killsPerLevel);
}

export function walkSpeedFor(level: number): number {
  const v = Math.min(T.walkSpeed.max, T.walkSpeed.base + T.walkSpeed.perLevel * level);
  return toward(v, v * (1 + T.overtime.speed), level);
}

export function arrivalGapFor(level: number): number {
  const gap = Math.max(T.arrivalGap.min, T.arrivalGap.base - T.arrivalGap.perLevel * level);
  return toward(gap, T.overtime.gap, level);
}

/** Extra arrival time after a kind so its pattern fits; tighter in overtime. */
export function extraGapFor(kind: EnemyKind, level: number): number {
  const extra = KINDS[kind].extraGap;
  return toward(extra, extra * T.overtime.extra, level);
}

/** How fast an enemy is closing right now (a launching kick is much faster). */
export function closingSpeed(e: Enemy): number {
  return e.state === 'launch' ? KICK.launchSpeed : e.speed;
}

/** True when a strike at `e` would connect (inside reach, with early grace). */
export function inReach(e: Enemy): boolean {
  return e.d <= T.reach + closingSpeed(e) * T.earlyGrace;
}

/** Opponents a round brings, its boss included (null when endless). */
export function opponentsLeft(spec: RoundSpec): number | null {
  return spec.count === null ? null : spec.count + (spec.boss ? 1 : 0);
}

export function comboMultiplier(combo: number): number {
  return Math.min(T.comboMaxMult, 1 + Math.floor(combo / T.comboStep));
}

/** The PERFECT chain's bonus tier: one more every `chain.step` hits, capped. */
export function chainTier(chain: number): number {
  return Math.min(T.chain.maxTier, Math.floor(chain / T.chain.step));
}

export class Sim {
  readonly state: SimState;
  readonly maxHearts: number;
  private readonly kiaiRate: number;
  private readonly rng: Rng;
  private events: SimEvent[] = [];
  private nextId = 1;
  /** Sim time at which the next enemy should reach striking distance. */
  private nextArrival = 1.2;
  private lastSide: Side = 1;
  private sameSideRun = 0;
  private spec: RoundSpec;
  /** A tap made while locked out, replayed when the lockout ends. */
  private buffered: { side: Side; until: number } | null = null;
  private spawned = 0;
  /** A planned pattern's members still to enter, in order, at the level they were planned for. */
  private queue: (PlannedSpawn & { phase: number; level: number })[] = [];
  /** A single opponent drawn but waiting for room on a crowded side. */
  private held: { kind: EnemyKind; side: Side } | null = null;
  /** When the round's boss enters (once the mat is clear), and whether he has. */
  private bossAt: number | null = null;
  private bossIn = false;

  constructor(rng: Rng, spec: RoundSpec = ENDLESS, opts: SimOptions = {}) {
    this.rng = rng;
    this.spec = spec;
    this.maxHearts = opts.hearts ?? T.hearts;
    this.kiaiRate = opts.kiaiRate ?? 1;
    this.state = {
      time: 0,
      running: true,
      hearts: this.maxHearts,
      score: 0,
      combo: 0,
      bestCombo: 0,
      chain: 0,
      bestChain: 0,
      kills: 0,
      hits: 0,
      perfects: 0,
      kiai: 0,
      player: { state: 'idle', stateT: 0, side: 1, strikes: 0 },
      invuln: 0,
      enemies: [],
      level: spec.level,
      remaining: opponentsLeft(spec),
      inRound: true,
    };
  }

  /** Start the next round of a match. Score, hearts, combo and kiai carry over. */
  beginRound(spec: RoundSpec): void {
    const s = this.state;
    this.spec = spec;
    this.spawned = 0;
    this.queue = [];
    this.held = null;
    this.bossAt = null;
    this.bossIn = false;
    this.nextArrival = s.time + 1.2;
    s.enemies = [];
    this.buffered = null;
    s.level = spec.level;
    s.remaining = opponentsLeft(spec);
    s.inRound = true;
    s.running = s.hearts > 0;
  }

  /** Give back a heart (between exam rounds), capped at the maximum. */
  heal(amount = 1): void {
    this.state.hearts = Math.min(this.maxHearts, this.state.hearts + amount);
  }

  /** Events produced since the last drain (spawns, hits, hurts…). */
  drainEvents(): SimEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }

  /** The enemy that a strike to `side` would connect with, if any. A boss
   *  being knocked back can't be: each of his acts has to be read, not mashed. */
  front(side: Side): Enemy | undefined {
    let best: Enemy | undefined;
    for (const e of this.state.enemies) {
      if (e.side !== side || e.state === 'dead' || (e.boss && e.state === 'recoil')) continue;
      if (!best || e.d < best.d) best = e;
    }
    return best;
  }

  step(dt: number = T.dt): void {
    const s = this.state;
    if (!s.running) return;
    s.time += dt;
    s.invuln = Math.max(0, s.invuln - dt);
    this.stepPlayer(dt);
    if (s.inRound) this.spawn();
    this.stepEnemies(dt);
    if (s.inRound && s.remaining === 0 && s.running) {
      s.inRound = false;
      this.events.push({ type: 'roundclear' });
    }
  }

  strike(side: Side): void {
    const s = this.state;
    if (!s.running) return;
    const p = s.player;
    if (p.state === 'recover' || p.state === 'hurt') {
      this.buffered = { side, until: s.time + T.inputBuffer };
      return;
    }
    p.side = side;
    p.strikes++;

    const target = this.front(side);
    if (!target || !inReach(target)) {
      p.state = 'recover';
      p.stateT = 0;
      s.combo = 0;
      s.chain = 0;
      this.events.push({ type: 'whiff', side });
      return;
    }

    p.state = 'strike';
    p.stateT = 0;

    if (target.kind === 'blocker' && target.state !== 'windup') {
      // Struck into the guard: the combo holds, the PERFECT chain doesn't
      s.chain = 0;
      this.pushBack(target, T.blockPush);
      this.events.push({ type: 'blocked', enemy: target });
      return;
    }

    let judgement: Judgement;
    if (target.state === 'windup') {
      judgement = target.kind === 'blocker' ? 'counter' : 'good';
    } else {
      const perfectBand = closingSpeed(target) * T.perfectWindow;
      judgement = target.d >= T.reach - perfectBand ? 'perfect' : 'good';
    }

    s.combo++;
    s.bestCombo = Math.max(s.bestCombo, s.combo);
    s.chain = judgement === 'good' ? 0 : s.chain + 1;
    s.bestChain = Math.max(s.bestChain, s.chain);
    const tier = chainTier(s.chain);
    const mult = comboMultiplier(s.combo);
    const points = (T.points[judgement] + T.chain.bonus * tier) * mult;
    s.score += points;
    s.hits++;
    if (judgement !== 'good') s.perfects++;
    const gain =
      judgement === 'perfect'
        ? T.kiaiPerPerfect
        : judgement === 'counter'
          ? T.kiaiPerCounter
          : T.kiaiPerGood;
    s.kiai = Math.min(1, s.kiai + (gain + T.chain.kiai * tier) * this.kiaiRate);

    target.hp--;
    const killed = target.hp <= 0;
    if (killed) this.kill(target);
    // A boss whose act took its last hit comes back as the next one
    else if (target.boss && --target.actHits! <= 0) this.nextAct(target);
    // The grappler shrugs a hit off and keeps coming; everyone else is knocked back
    else this.pushBack(target, target.kind === 'grappler' ? GRAPPLE.shrug : T.knockback);
    this.events.push({ type: 'hit', enemy: target, judgement, points, killed, chain: s.chain });
  }

  /** Spend a full Kiai meter: every enemy on the mat goes down. */
  kiai(): boolean {
    const s = this.state;
    if (!s.running || s.kiai < 1) return false;
    s.kiai = 0;
    let killed = 0;
    for (const e of s.enemies) {
      if (e.state === 'dead' || e.d > T.spawnDistance) continue;
      killed++;
      // A boss isn't floored by it: the shout ends the act he's in
      if (e.boss) {
        e.hp -= e.actHits!;
        if (e.hp > 0) {
          this.nextAct(e);
          continue;
        }
      }
      this.kill(e);
    }
    const points = killed * T.points.kiai * comboMultiplier(s.combo);
    s.score += points;
    this.events.push({ type: 'kiai', killed, points });
    return true;
  }

  private kill(e: Enemy): void {
    e.hp = 0;
    e.state = 'dead';
    e.stateT = 0;
    const s = this.state;
    s.kills++;
    if (s.remaining !== null) s.remaining--;
    if (this.spec.ramp) s.level = this.spec.level + levelFor(s.kills);
  }

  /** Knock a boss back and send him in as his next act. */
  private nextAct(e: Enemy): void {
    const acts = BOSSES[e.boss!].acts;
    e.act = Math.min(acts.length - 1, (e.act ?? 0) + 1);
    e.kind = acts[e.act]!;
    e.actHits = KINDS[e.kind].hp;
    e.speed = walkSpeedFor(this.state.level) * KINDS[e.kind].speedMult;
    e.windup = windupFor(e.kind, this.state.level);
    e.feinted = false;
    this.pushBack(e, BOSS.knockback);
    this.events.push({ type: 'act', enemy: e });
  }

  private pushBack(e: Enemy, amount: number): void {
    e.state = 'recoil';
    e.stateT = 0;
    e.recoilLeft = amount;
  }

  private stepPlayer(dt: number): void {
    const p = this.state.player;
    p.stateT += dt;
    const limit =
      p.state === 'strike'
        ? T.strikeTime
        : p.state === 'recover'
          ? T.whiffRecover
          : p.state === 'hurt'
            ? T.whiffRecover
            : Infinity;
    if (p.stateT >= limit) {
      p.state = 'idle';
      p.stateT = 0;
      this.replayBuffered();
    }
  }

  /** Fire a tap buffered during the lockout — but only at a real target, so a
   *  mashed early tap never costs a second whiff. */
  private replayBuffered(): void {
    const b = this.buffered;
    this.buffered = null;
    if (!b || this.state.time > b.until) return;
    const target = this.front(b.side);
    if (target && inReach(target)) this.strike(b.side);
  }

  /*
   * Spawning. The RNG is drawn only here, at decisions, and what can be drawn
   * depends only on the round, its level and how many opponents are left —
   * never on where opponents stand or what the player did — so a seed brings
   * the same opponents in the same order for everyone (the daily exam). A
   * round without patterns draws exactly as it always has.
   */
  private spawn(): void {
    const s = this.state;
    // A planned pattern enters first, each member at its moment
    if (this.queue.length) {
      while (this.queue.length && s.time >= this.queue[0]!.at) {
        const q = this.queue.shift()!;
        this.spawnEnemy(q.kind, q.side, q.phase, q.level);
      }
      return;
    }
    // One drawn already steps in as soon as there's room on his side
    if (this.held) {
      if (!this.roomOn(this.held.side)) return;
      this.spawnEnemy(this.held.kind, this.held.side);
      this.held = null;
    }
    if (this.spec.count !== null && this.spawned >= this.spec.count) {
      if (this.spec.boss && !this.bossIn) this.bossEntry(this.spec.boss);
      return;
    }
    const speed = walkSpeedFor(s.level);
    const lead = (T.spawnDistance - T.reach) / speed;
    if (s.time < this.nextArrival - lead) return;
    if (this.tryPattern(speed)) return;

    const kind = this.pickKind();
    const side = this.pickSide();
    // At the deepest levels arrivals can come faster than one side's queue
    // makes room: then he waits at the edge instead of squeezing in behind
    if (this.roomOn(side)) this.spawnEnemy(kind, side);
    else this.held = { kind, side };
    const jitter = 1 + T.arrivalGap.jitter * (this.rng.next() * 2 - 1);
    this.nextArrival =
      Math.max(this.nextArrival, s.time + lead) +
      arrivalGapFor(s.level) * jitter +
      extraGapFor(kind, s.level);
  }

  /** The boss walks in alone, a moment after the last opponent is down (no RNG). */
  private bossEntry(boss: BossId): void {
    const s = this.state;
    if (s.enemies.some((e) => e.state !== 'dead')) {
      this.bossAt = null;
      return;
    }
    this.bossAt ??= s.time + BOSS.entryDelay;
    if (s.time < this.bossAt) return;
    this.bossIn = true;
    const acts = BOSSES[boss].acts;
    const kind = acts[0]!;
    this.spawnEnemy(kind, -this.lastSide as Side);
    const e = s.enemies.at(-1)!;
    e.boss = boss;
    e.act = 0;
    e.actHits = KINDS[kind].hp;
    e.hp = acts.reduce((n, k) => n + KINDS[k].hp, 0);
  }

  /** Whether a newcomer on `side` can step in behind the last one there. */
  private roomOn(side: Side): boolean {
    return this.state.enemies.every(
      (e) => e.side !== side || e.state === 'dead' || e.d <= T.spawnDistance - T.queueSpacing,
    );
  }

  private spawnEnemy(kind: EnemyKind, side: Side, phase?: number, level = this.state.level): void {
    const def = KINDS[kind];
    const enemy: Enemy = {
      id: this.nextId++,
      kind,
      side,
      d: T.spawnDistance,
      speed: walkSpeedFor(level) * def.speedMult,
      windup: windupFor(kind, level),
      hp: def.hp,
      state: 'walk',
      stateT: 0,
      feinted: false,
      recoilLeft: 0,
      ...(phase !== undefined ? { phase } : {}),
    };
    this.state.enemies.push(enemy);
    this.spawned++;
    this.noteSide(side);
    this.events.push({ type: 'spawn', enemy });
  }

  /** Maybe send an attack pattern for this arrival; true if one was queued. */
  private tryPattern(walk: number): boolean {
    const s = this.state;
    let share = this.spec.patterns ?? 0;
    if (share > 0 && this.spec.ramp)
      share = Math.min(PATTERN.rampMax, share + PATTERN.rampPerLevel * (s.level - this.spec.level));
    const left = this.spec.count === null ? Infinity : this.spec.count - this.spawned;
    if (share <= 0 || left < 2 || this.rng.next() >= share) return false;
    const ids = eligiblePatterns(this.spec, s.level, left);
    if (ids.length === 0) return false;
    let r = this.rng.next() * ids.reduce((n, id) => n + PATTERNS[id].weight, 0);
    const id = ids.find((x) => (r -= PATTERNS[x].weight) < 0) ?? ids[0]!;
    const def = PATTERNS[id];
    const mirror: Side = this.rng.next() < 0.5 ? -1 : 1;
    const kinds = def.steps.map(([k]) => k ?? this.pickKind(true));
    const beat = this.spec.beat ?? PATTERN.defaultBeat;
    const plan = (m: Side) =>
      planPattern(
        def,
        kinds,
        def.steps.map(([, side]) => (side === 0 ? m : (-m as Side))),
        walk,
        beat,
        s.time,
        this.nextArrival,
        s.level,
      );
    // Never more than maxSameSide in a row: start from the other side instead
    let p = plan(mirror);
    if (this.runTooLong(p.spawns)) p = plan(-mirror as Side);
    const phase = this.nextId * 0.37;
    this.queue = p.spawns.map((q) => ({ ...q, phase, level: s.level }));
    const last = kinds.at(-1)!;
    this.nextArrival =
      p.lastStrike + arrivalGapFor(s.level) + extraGapFor(last, s.level) + def.rest * beat;
    this.spawn();
    return true;
  }

  private runTooLong(spawns: readonly PlannedSpawn[]): boolean {
    let last = this.lastSide;
    let run = this.sameSideRun;
    for (const { side } of spawns) {
      run = side === last ? run + 1 : 1;
      last = side;
      if (run > T.maxSameSide) return true;
    }
    return false;
  }

  /** Weighted draw from the round's mix; `oneHit` keeps to kinds that take one strike. */
  private pickKind(oneHit = false): EnemyKind {
    const level = this.state.level;
    const pool: [EnemyKind, number][] = [];
    let total = 0;
    if (this.spec.kinds) {
      for (const [kind, w] of Object.entries(this.spec.kinds) as [EnemyKind, number][]) {
        if (oneHit && KINDS[kind].hp > 1) continue;
        pool.push([kind, w]);
        total += w;
      }
    } else
      for (const [kind, def] of Object.entries(KINDS) as [EnemyKind, (typeof KINDS)[EnemyKind]][]) {
        if (level < def.fromLevel || (oneHit && def.hp > 1)) continue;
        // New kinds ramp in over two levels so the first ones are easy to read;
        // in overtime the plain street fighter makes way for the trickier kinds
        const thin = kind === 'basic' ? toward(1, T.overtime.basic, level) : 1;
        const w = def.weight * Math.min(1, (level - def.fromLevel + 1) / 2) * thin;
        pool.push([kind, w]);
        total += w;
      }
    let r = this.rng.next() * total;
    for (const [kind, w] of pool) {
      r -= w;
      if (r < 0) return kind;
    }
    return 'basic';
  }

  private pickSide(): Side {
    const side: Side = this.rng.next() < 0.5 ? -1 : 1;
    return side === this.lastSide && this.sameSideRun >= T.maxSameSide ? (-side as Side) : side;
  }

  private noteSide(side: Side): void {
    this.sameSideRun = side === this.lastSide ? this.sameSideRun + 1 : 1;
    this.lastSide = side;
  }

  private stepEnemies(dt: number): void {
    const s = this.state;
    // Front-to-back per side so each enemy can queue behind the one ahead
    const sorted = [...s.enemies].sort((a, b) => a.d - b.d);
    const ahead: Record<Side, number> = { [-1]: 0, [1]: 0 };

    for (const e of sorted) {
      e.stateT += dt;
      if (e.state === 'dead') continue;
      const minD = ahead[e.side] > 0 ? ahead[e.side] + T.queueSpacing : T.attackRange;
      const isFront = ahead[e.side] === 0;

      switch (e.state) {
        case 'walk': {
          // Footwork: speed pulses per kind around its average (phase offset per opponent)
          // Inside reach the grappler presses in low and slower, giving time for two hits
          const press = e.kind === 'grappler' && isFront && e.d <= T.reach ? GRAPPLE.press : 1;
          const phase = e.phase ?? e.id * 0.37;
          e.d = Math.max(minD, e.d - e.speed * gait(e.kind, e.stateT + phase) * press * dt);
          if (isFront && e.kind === 'feinter' && !e.feinted && e.d <= T.reach + FEINT.margin) {
            e.state = 'feint';
            e.stateT = 0;
            e.feinted = true;
            this.events.push({ type: 'feint', enemy: e });
          } else if (isFront && e.kind === 'kicker' && e.d <= T.reach + KICK.stand) {
            // Kicker chambers from outside reach
            e.state = 'windup';
            e.stateT = 0;
            this.events.push({ type: 'windup', enemy: e });
          } else if (isFront && e.d <= T.attackRange) {
            e.state = 'windup';
            e.stateT = 0;
            this.events.push({ type: 'windup', enemy: e });
          }
          break;
        }
        case 'feint':
          if (e.stateT >= FEINT.duration) {
            e.state = 'walk';
            e.stateT = 0;
            e.speed *= FEINT.rushMult;
          }
          break;
        case 'windup':
          // The player gets breathing room after a hit: windups hold still
          if (s.invuln > 0) e.stateT = 0;
          else if (e.stateT >= e.windup) {
            if (e.kind === 'kicker') {
              e.state = 'launch';
              e.stateT = 0;
            } else this.landAttack(e);
          }
          break;
        case 'launch':
          // The kick flies in; it lands if nothing stops it on the way
          if (s.invuln > 0) {
            e.state = 'walk';
            e.stateT = 0;
            break;
          }
          e.d = Math.max(T.attackRange, e.d - KICK.launchSpeed * dt);
          if (e.d <= T.attackRange) this.landAttack(e);
          break;
        case 'recoil': {
          const move = Math.min(e.recoilLeft, T.recoilSpeed * dt);
          e.d += move;
          e.recoilLeft -= move;
          if (e.recoilLeft <= 0) {
            e.state = 'walk';
            e.stateT = 0;
          }
          break;
        }
      }
      ahead[e.side] = e.d;
    }

    s.enemies = s.enemies.filter((e) => e.state !== 'dead' || e.stateT < T.deadTime);
  }

  private landAttack(attacker: Enemy): void {
    const s = this.state;
    s.hearts--;
    s.combo = 0;
    s.chain = 0;
    s.invuln = T.hurtInvuln;
    s.player.state = 'hurt';
    s.player.stateT = 0;
    s.player.side = attacker.side;
    for (const e of s.enemies) {
      if (e.state !== 'dead' && e.d < T.hurtPushTo) {
        this.pushBack(e, T.hurtPushTo - e.d);
      }
    }
    this.events.push({ type: 'hurt', enemy: attacker, hearts: s.hearts });
    if (s.hearts <= 0) {
      s.running = false;
      this.events.push({ type: 'gameover' });
    }
  }
}
